import { Descriptions, DescriptionsProps, Modal, Result } from "antd"
import { PaymentForm } from "./PaymentForm"
import { useDispatch, useSelector } from "react-redux"
import { updateCurrentContract } from "src/features/ServiceContract/state/action"
import { ServiceDebitContract } from "src/common/models/serviceContractDebit"
import { createContractPaymentApi } from "src/features/CustomerPayment/state/action"
import { ServiceContractPayment } from "src/common/models/serviceContractDebitPayment"
import { RootState } from "src/state/reducer"
import { useEffect, useState } from "react"
import { SystemDescriptions } from "src/common/descriptions"
import { formatToCurrency, formatToDisplayDatetime } from "src/common/util"
import { QuestionOutlined } from '@ant-design/icons';
import { FormActionButtons } from "src/components/FormActionButtons"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { CREATE_CONTRACT_PAYMENT_API } from "src/features/CustomerPayment/state/actionTypes"
import { TransactionContractPayment } from "src/common/models/transactionContractPayment"

interface Props {
    open: boolean
    onCancel: VoidFunction
    onAccept: VoidFunction
    contractDebit?: ServiceDebitContract
}

export type ReduxProps = {
    createContractPaymentSuccess?: boolean
    isCreatingPayment: boolean
}

export const CreatePayment = (props: Props) => {
    const dispatch = useDispatch()

    const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)
    const [paymentData, setPaymentData] = useState<ServiceContractPayment>()

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        createContractPaymentSuccess: state.contractPayment.createContractPaymentSuccess,
        isCreatingPayment: serviceLoading(state, [CREATE_CONTRACT_PAYMENT_API]),
    }))

    const descriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT

    useEffect(() => {
        if (reduxProps.createContractPaymentSuccess && actionButtonClicked) {
            props.onAccept()
        }
    }, [reduxProps.createContractPaymentSuccess])

    const onFinish = (payment: TransactionContractPayment) => {

        const paymentData: ServiceContractPayment = {
            ...payment,
            serviceContractDebitId: payment.contractId,
            datetime: '',
            userId: 0,
        }

        if (props.contractDebit) {
            setPaymentData(paymentData)
            setShowConfirmation(true)
        } else {
            dispatch(updateCurrentContract({
                payment: paymentData,
            }))
            props.onAccept()
        }
    }


    const items: DescriptionsProps['items'] = [
        {
            key: 'CUSTOMER',
            label: descriptions.DESCRIPTIONS.CUSTOMER,
            children: props.contractDebit?.customer,
        },
        {
            key: 'DESCRIPTION',
            label: descriptions.DESCRIPTIONS.DESCRIPTION,
            children: props.contractDebit?.description,
        },
        {
            key: 'CREATED_AT',
            label: descriptions.DESCRIPTIONS.CREATED_AT,
            children: formatToDisplayDatetime(props.contractDebit?.createdAt),
        },
        {
            key: 'AMOUN',
            label: descriptions.DESCRIPTIONS.AMOUNT,
            children: formatToCurrency(props.contractDebit?.amount.toString() || '0'),
        },
        {
            key: 'AMOUNT_PAYED',
            label: descriptions.DESCRIPTIONS.AMOUNT_PAYED,
            children: formatToCurrency(props.contractDebit?.amountPayed.toString() || '0'),
        },
        {
            key: 'OUTSTANDING',
            label: descriptions.DESCRIPTIONS.OUTSTANDING,
            children: formatToCurrency((Number(props.contractDebit?.amount) - Number(props.contractDebit?.amountPayed)).toString()),
        },
    ];

    const renderDebitData = () => {
        return <Descriptions items={items} />
    }

    const paymentSummary = () => {

        const summaryItems: DescriptionsProps['items'] = [
            {
                key: 'CUSTOMER',
                label: descriptions.DESCRIPTIONS.CUSTOMER,
                children: props.contractDebit?.customer,
            },
            {
                key: 'DESCRIPTION',
                label: descriptions.DESCRIPTIONS.DESCRIPTION,
                children: props.contractDebit?.description,
            },
            {
                key: 'AMOUN',
                label: descriptions.DESCRIPTIONS.AMOUNT,
                children: formatToCurrency(props.contractDebit?.amount.toString() || '0'),
            },
            {
                key: 'AMOUNT_PAYED',
                label: descriptions.DESCRIPTIONS.AMOUNT_PAYED,
                children: formatToCurrency(props.contractDebit?.amountPayed.toString() || '0'),
            },
            {
                key: 'OUTSTANDING',
                label: descriptions.DESCRIPTIONS.OUTSTANDING,
                children: formatToCurrency((Number(props.contractDebit?.amount) - Number(props.contractDebit?.amountPayed)).toString()),
                span: 2,
            },
            {
                key: 'CASH',
                label: descriptions.FORM.CASH.LABEL,
                children: formatToCurrency(paymentData?.cash?.toString() || '0'),
            },
            {
                key: 'TRANSACTION',
                label: descriptions.FORM.TRANSACTION.LABEL,
                children: formatToCurrency(paymentData?.transaction?.toString() || '0'),
            },
            {
                key: 'DISSCOUNT',
                label: descriptions.FORM.DISSCOUNT.LABEL,
                children: formatToCurrency(paymentData?.disscount?.toString() || '0'),
            },
            {
                key: 'TOTAL',
                label: descriptions.FORM.PAYMENT_TOTAL.LABEL,
                children: formatToCurrency(paymentData?.amount.toString() || '0'),
            },
            {
                key: 'COMMENT',
                label: descriptions.FORM.COMMENT.LABEL,
                children: paymentData?.comment || 'NA',
                span: 2,
            },

        ];

        return <div style={{ marginBottom: 12, }}>
            <Descriptions items={summaryItems} />
        </div>
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
            actionText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
            onCancel={function (): void {
                setShowConfirmation(false)
            }}
            onAccept={() => {
                setActionButtonClicked(true)
                dispatch(createContractPaymentApi(paymentData as ServiceContractPayment))
            }}
            actionLoading={reduxProps.isCreatingPayment}
        />
    )

    const renderConfirmation = () => {
        return <Result
            icon={<QuestionOutlined />}
            title={descriptions.CONFIRM_MODAL.TITLE}
            subTitle={descriptions.CONFIRM_MODAL.DESCRIPTION}
            extra={[
                paymentSummary(),
                renderButtons(),
            ]}
        />
    }

    return (
        <Modal
            title={descriptions.TITLE}
            centered
            open={props.open}
            width={1000}
            onOk={props.onAccept}
            okText={descriptions.SAVE_BUTTON}
            cancelText={descriptions.CANCEL_BUTTON}
            footer={<></>}
            closable={false}
            zIndex={1200}
            getContainer={false}
            destroyOnClose={true}
        >
            {
                showConfirmation
                    ? renderConfirmation()
                    :
                    <>
                        {
                            props.contractDebit &&
                            <>
                                {renderDebitData()}
                                <hr />
                            </>
                        }

                        <PaymentForm
                            onFinish={onFinish}
                            onCancel={props.onCancel}
                            contractDebit={props.contractDebit}
                            type="ACCOUNT_RECEIVABLES"
                        />
                    </>
            }
        </Modal>
    )
}
